import { Navigate, Outlet, useLocation } from "react-router-dom"
import { useAppSelector } from "app/hooks"
import { useEffect } from "react"
import PATHS from "router/paths"
import FullPageLoading from "./FullPageLoading"
import { useGoal, useUser } from "features/onboarding/hooks"
import styled from "styled-components"
import { isInWebView } from "utils/webViewDetection"
import { useFitProfile } from "features/auth/hooks"
import FitBanner from "components/Layouts/FitBanner"
import TabHeader from "./TabHeader"
import AppOutdatePopConfirm from "components/AppOutDatePopup"
import { useInitDiaryDateParamsOnUrl } from "features/home/hooks"
import { REDIRECT_URL_PARAM } from "features/auth/Components/LoginInNativeApp/hooks"
import { useBodyDisplayUnits } from "features/profile/hooks/body"

const StyledAppContent = styled.div`
  height: 100%;
  background-color: var(--Schemes-Surface-Container-Low, #f5f7f7);
`

const ProtectedLayout = () => {
  useUser()
  useInitDiaryDateParamsOnUrl()
  useBodyDisplayUnits()

  const location = useLocation()

  const isAppOutDated = useAppSelector(
    (state) => state.notification.isAppOutDated,
  )

  const { goal, isNotHasGoalYet } = useGoal()
  const { isNotHasFitProfileYet, fitProfile } = useFitProfile()

  const shouldShowFitBanner = useAppSelector(
    (state) => state.notification.shouldShowFitBanner,
  )
  const isPageHasFitBanner = [PATHS.app.overview, PATHS.app.root].includes(
    location.pathname,
  )

  const hasFitBanner = shouldShowFitBanner && isPageHasFitBanner

  if (!fitProfile) {
    if (
      isNotHasFitProfileYet &&
      PATHS.app.profile.createAccount !== location.pathname
    ) {
      return <Navigate to={PATHS.app.profile.createAccount} />
    }

    if (!isNotHasFitProfileYet) {
      return <FullPageLoading />
    }
  }

  if (fitProfile && !goal) {
    if (!isNotHasGoalYet) {
      return <FullPageLoading />
    }

    const validPaths = [PATHS.app.personalInfo, PATHS.app.setGoal]
    if (isInWebView()) {
      validPaths.push(PATHS.app.welcomeOnboarding)
    }

    if (!validPaths.includes(location.pathname)) {
      if (isInWebView()) {
        return <Navigate to={PATHS.app.welcomeOnboarding} />
      } else {
        return <Navigate to={PATHS.app.personalInfo} />
      }
    }
  }

  if (
    fitProfile &&
    goal &&
    [PATHS.app.personalInfo, PATHS.app.profile.createAccount].includes(
      location.pathname,
    )
  ) {
    return <Navigate to={PATHS.app.root} />
  }

  const hasTopNavigationPaths = [
    PATHS.app.root,
    PATHS.app.myFoods.root,
    PATHS.app.myFoods.schedules,
    PATHS.app.myFoods.recipes,
    PATHS.app.yourGoal.root,
  ]

  return (
    <StyledAppContent>
      {hasFitBanner && <FitBanner />}
      {hasTopNavigationPaths.includes(location.pathname) && <TabHeader />}
      {isAppOutDated && <AppOutdatePopConfirm />}

      <Outlet />
    </StyledAppContent>
  )
}

const AppLayout = () => {
  const token = useAppSelector((state) => state.auth.token)
  const location = useLocation()

  if (!token) {
    const redirectUrl = encodeURIComponent(
      location.pathname + location.search + location.hash,
    )
    return (
      <Navigate
        to={`${PATHS.auth.loginInNativeApp}?${REDIRECT_URL_PARAM}=${redirectUrl}`}
      />
    )
  }

  return <ProtectedLayout />
}

export default AppLayout
